import type { ProfileDTO, ProfilePatchParams, ProfilePatchResponse, UserDTO, UserMeResponse } from '@/api/schema'
import { api } from '@/app/utils/api'
import { getBotStorageKey } from '@/app/utils/storage'
import { createContext } from 'preact'
import { useContext } from 'preact/hooks'
import { create, type StoreApi, type UseBoundStore } from 'zustand'

export type SessionState = {
  user: UserDTO | null
  profile: ProfileDTO | null
  ready: boolean
  init(initialSession?: UserMeResponse | null): Promise<void>
  reset(): void
  clean(): void
  fetch(): Promise<void>
  sync(user: UserDTO, profile?: ProfileDTO | null): void
  patchProfile(data: ProfilePatchParams): Promise<void>
  logout(): Promise<void>
}

export type SessionStore = UseBoundStore<StoreApi<SessionState>>

export const SessionStoreContext = createContext<SessionStore | null>(null)

export function useSession<T>(selector: (state: SessionState) => T) {
  const store = useContext(SessionStoreContext)

  if (!store) {
    throw new Error('SessionProvider is required')
  }

  return store(selector)
}

export const createSessionStore = () => (
  create<SessionState>((set, get) => ({
    ready: false,
    user: null,
    profile: null,

    async init(initialSession) {
      if (initialSession) {
        set(getReadySessionState(initialSession))
        return
      }

      if (initialSession === null) {
        api.get<UserMeResponse>('/user/me')
          .then(resp => {
            if (resp?.data.user) {
              set({
                ready: true,
                user: resp.data.user,
                profile: resp.data.profile ?? null,
              })
            }
          })
          .catch(console.error)

        return
      }

      const resp = await api.get<UserMeResponse>('/user/me')
        .catch(e => {
          console.error(e)
          return null
        })

      set({
        ready: true,
        user: resp?.data.user ?? null,
        profile: resp?.data.profile ?? null,
      })
    },

    reset() {
      set({
        user: null,
        profile: null,
        ready: false,
      })
    },

    async fetch() {
      const resp = await api.get<UserMeResponse>('/user/me')

      set({
        user: resp.data.user,
        profile: resp.data.profile,
      })
    },

    sync(user, profile) {
      set({
        user,
        profile: profile ?? null,
      })
    },

    async patchProfile(data) {
      const user = get().user

      let respData: ProfilePatchResponse

      if (!user) {
        const resp = await api.post<ProfilePatchResponse>(
          '/user/create',
          data satisfies ProfilePatchParams,
        )
        respData = resp.data
      }
      else {
        const resp = await api.patch<ProfilePatchResponse>(
          '/user/profile',
          data satisfies ProfilePatchParams,
        )
        respData = resp.data
      }

      set({
        profile: respData.profile,
        user: respData.user,
      })
    },

    clean() {
      set({
        user: null,
        profile: null,
      })

      try {
        localStorage.removeItem(getBotStorageKey('eva'))
        localStorage.removeItem(getBotStorageKey('milana'))
        localStorage.removeItem(getBotStorageKey('zoe'))
      }
      catch (e) {
        console.error(e)
      }
    },

    async logout() {
      await api.post('/user/logout')
      get().clean()
    },
  }))
)

export function getReadySessionState(session: UserMeResponse | null) {
  return {
    ready: true,
    user: session?.user ?? null,
    profile: session?.profile ?? null,
  }
}
